/**
 * SEO.js — Per-page SEO + GEO (Generative Engine Optimization) component
 *
 * Usage:
 *   <SEO
 *     title="Generate Content Ideas | Postcraft"
 *     description="AI-powered content idea generator for Instagram, Twitter, LinkedIn. Get 5 unique, platform-native post ideas in seconds."
 *     path="/app/ideas"
 *   />
 *
 * GEO signals this component injects:
 *  - Per-page <title> → unique, descriptive, keyword-rich
 *  - Per-page meta description → concise, entity-rich
 *  - Canonical URL → prevents duplicate content penalty
 *  - og:title / og:description → social sharing previews
 *  - Structured breadcrumb data (JSON-LD) → AI engines understand site hierarchy
 */

import { useEffect } from 'react';

const BASE_URL = 'https://socialixer.com';

const DEFAULT_META = {
    title: 'Socialixer — AI Social Media Automation Platform',
    description:
        'Socialixer is an AI-powered social media automation platform. Schedule posts, discover content gaps, generate ideas, track analytics, and manage leads across Instagram, Twitter, LinkedIn, YouTube, Facebook, Reddit, and TikTok.',
    image: `${BASE_URL}/og-image.png`,
};

function updateTag(selector, attr, value) {
    let el = document.querySelector(selector);
    if (!el) {
        el = document.createElement('meta');
        // Parse selector to set attributes
        const [, name, nameVal] = selector.match(/\[([^=]+)="([^"]+)"\]/) || [];
        if (name) el.setAttribute(name, nameVal);
        document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
}

function updateLd(id, data) {
    let script = document.getElementById(id);
    if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data, null, 2);
}

export default function SEO({
    title,
    description,
    path = '/',
    image,
    type = 'WebPage',
    breadcrumbs = null,
    noIndex = false,
}) {
    const resolvedTitle = title || DEFAULT_META.title;
    const resolvedDesc = description || DEFAULT_META.description;
    const resolvedImage = image || DEFAULT_META.image;
    const canonicalUrl = `${BASE_URL}${path}`;

    useEffect(() => {
        // ── Title ────────────────────────────────────────────────────────────────
        document.title = resolvedTitle;

        // ── Meta description ─────────────────────────────────────────────────────
        updateTag('meta[name="description"]', 'content', resolvedDesc);

        // ── Robots ───────────────────────────────────────────────────────────────
        updateTag('meta[name="robots"]', 'content', noIndex ? 'noindex, nofollow' : 'index, follow');

        // ── Canonical ────────────────────────────────────────────────────────────
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = canonicalUrl;

        // ── Open Graph ───────────────────────────────────────────────────────────
        updateTag('meta[property="og:title"]', 'content', resolvedTitle);
        updateTag('meta[property="og:description"]', 'content', resolvedDesc);
        updateTag('meta[property="og:image"]', 'content', resolvedImage);
        updateTag('meta[property="og:url"]', 'content', canonicalUrl);
        updateTag('meta[property="og:type"]', 'content', type === 'Article' ? 'article' : 'website');

        // ── Twitter Card ─────────────────────────────────────────────────────────
        updateTag('meta[name="twitter:title"]', 'content', resolvedTitle);
        updateTag('meta[name="twitter:description"]', 'content', resolvedDesc);
        updateTag('meta[name="twitter:image"]', 'content', resolvedImage);

        // ── JSON-LD Breadcrumb (GEO signal — AI engines parse this for context) ──
        if (breadcrumbs && breadcrumbs.length > 0) {
            const ldBreadcrumb = {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                'itemListElement': [
                    { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
                    ...breadcrumbs.map((bc, idx) => ({
                        '@type': 'ListItem',
                        position: idx + 2,
                        name: bc.name,
                        item: `${BASE_URL}${bc.path}`,
                    })),
                ],
            };
            updateLd('ld-breadcrumb', ldBreadcrumb);
        }
    }, [resolvedTitle, resolvedDesc, resolvedImage, canonicalUrl, noIndex, breadcrumbs, type]);

    return null; // renders nothing — only side-effects
}
